import { Slot } from '@radix-ui/react-slot';
import { ComponentProps } from 'react';

import { ChevronRightIcon } from '@/assets/icons';
import { mergeClasses } from '@/utils';

const Breadcrumb = ({ ...props }: ComponentProps<'nav'>) => {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
};

const BreadcrumbList = ({ className, ...props }: ComponentProps<'ol'>) => {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={mergeClasses(
        'text-muted-foreground flex flex-wrap items-center text-sm break-words gap-1.5',
        className,
      )}
      {...props}
    />
  );
};

const BreadcrumbItem = ({ className, ...props }: ComponentProps<'li'>) => {
  return (
    <li
      data-slot="breadcrumb-item"
      className={mergeClasses('inline-flex items-center gap-1.5', className)}
      {...props}
    />
  );
};

const BreadcrumbLink = ({
  asChild,
  className,
  ...props
}: ComponentProps<'a'> & {
  asChild?: boolean;
}) => {
  const Component = asChild ? Slot : 'a';

  return (
    <Component
      data-slot="breadcrumb-link"
      className={mergeClasses(
        'text-brand-primary-base hover:text-brand-primary-dark hover:text-deco hover:underline transition-colors font-medium text-sm',
        className,
      )}
      {...props}
    />
  );
};

const BreadcrumbPage = ({ className, ...props }: ComponentProps<'span'>) => {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={mergeClasses('font-medium text-neutral-n7 text-sm', className)}
      {...props}
    />
  );
};

const BreadcrumbSeparator = ({ children, className, ...props }: ComponentProps<'li'>) => {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={mergeClasses('[&>svg]:size-3.5 text-neutral-n5', className)}
      {...props}
    >
      {children ?? <ChevronRightIcon />}
    </li>
  );
};

export {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
};
